import { Box, Button, Wrap, WrapItem, useToast } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import React from "react";

import { auth } from "../../../../core/auth";
import { env } from "../../../../env";
import RangeDatePicker from "../../../../shared/components/DatePicker/RangeDatePicker";
import ContractTypeSelect from "../../../../shared/components/ContractTypeSelect";
import OfficesSelect from "../../../../shared/components/OfficeSelect";
import Select from "../../../../shared/components/Select";
import { useDebounce } from "../../../../shared/hooks/useDebounce";
import {
  CaregiverId,
  ContractTypeId,
  OfficeId,
  PatientId,
  PatientTaskTemplateId,
} from "../../../../shared/schema/schema";
import { useSearchCaregiversQuery } from "../../../caregiver/hooks/useSearchCaregiversQuery";
import { useSearchPatientsQuery } from "../../../patient/hooks/useSearchPatientsQuery";
import { PatientTaskNewStatus, useTasksNewFilters } from "../hooks/useTasksNewFilters";

const MAX_DATE_RANGE_DAYS = 30;

const STATUS_OPTIONS: { value: PatientTaskNewStatus; label: string }[] = [
  { value: "APPROVED", label: "Approved" },
  { value: "COMPLETED", label: "Completed" },
  { value: "INCOMPLETED", label: "Incompleted" },
];

type PatientTaskTemplateOption = {
  id: PatientTaskTemplateId;
  title: string;
};

// Exported so the page can offer the same template list to the AI omnibar that this bar shows.
export function usePatientTaskTemplatesQuery() {
  return useQuery({
    queryKey: ["patientTaskTemplates", "list"],
    queryFn: async (): Promise<PatientTaskTemplateOption[]> => {
      const authState = auth.getAuthOrFail();
      const { data } = await axios.get<{ patientTaskTemplates: PatientTaskTemplateOption[] }>(
        `${env.API_URL}agencies/${authState.data.agency.id}/agency_members/${authState.data.agencyMember.id}/patient_task_templates`,
        { headers: { Authorization: `Token ${authState.tokens.authToken}` } }
      );
      return data.patientTaskTemplates;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export default function TasksNewFilters() {
  const filters = useTasksNewFilters();
  const toast = useToast();

  const templateQuery = usePatientTaskTemplatesQuery();

  // Caregiver async search state
  const [caregiverSearchTerm, setCaregiverSearchTerm] = React.useState("");
  const debouncedCaregiverTerm = useDebounce(caregiverSearchTerm, 300);
  const caregiverSearchQuery = useSearchCaregiversQuery({ term: debouncedCaregiverTerm });

  // Patient async search state
  const [patientSearchTerm, setPatientSearchTerm] = React.useState("");
  const debouncedPatientTerm = useDebounce(patientSearchTerm, 300);
  const patientSearchQuery = useSearchPatientsQuery({ term: debouncedPatientTerm });

  function handleDateRangeChange(value: [LocalDate, LocalDate | null] | null) {
    if (value === null) {
      filters.setValues({ scheduledFrom: null, scheduledTo: null, page: 1 });
      return;
    }
    const [from, to] = value;
    if (to === null) {
      filters.setValues({ scheduledFrom: from, scheduledTo: null, page: 1 });
      return;
    }
    const maxAllowedEnd = from.plusDays(MAX_DATE_RANGE_DAYS);
    if (to.isAfter(maxAllowedEnd)) {
      toast({
        title: `Date range capped at ${MAX_DATE_RANGE_DAYS} days`,
        description: `End date adjusted to ${maxAllowedEnd.toString()}.`,
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      filters.setValues({ scheduledFrom: from, scheduledTo: maxAllowedEnd, page: 1 });
      return;
    }
    filters.setValues({ scheduledFrom: from, scheduledTo: to, page: 1 });
  }

  function handleStatusChange(value: PatientTaskNewStatus[] | undefined) {
    filters.setValues({ statuses: value ?? [], page: 1 });
  }

  function handleTaskTemplateChange(value: PatientTaskTemplateId[] | undefined) {
    filters.setValues({ taskTemplateIds: value ?? [], page: 1 });
  }

  function handleCaregiverChange(value: CaregiverId[] | undefined) {
    filters.setValues({ caregiverIds: value ?? [], page: 1 });
  }

  function handlePatientChange(value: PatientId[] | undefined) {
    filters.setValues({ patientIds: value ?? [], page: 1 });
  }

  function handleContractChange(value: ContractTypeId[] | undefined) {
    filters.setValues({ contractTypeIds: value ?? [], page: 1 });
  }

  function handleOfficeChange(value: OfficeId[] | undefined) {
    filters.setValues({ officeIds: value ?? [], page: 1 });
  }

  const scheduledFrom = filters.getValue("scheduledFrom") ?? null;
  const scheduledTo = filters.getValue("scheduledTo") ?? null;
  const statuses = filters.getValue("statuses") ?? [];
  const taskTemplateIds = filters.getValue("taskTemplateIds") ?? [];
  const caregiverIds = filters.getValue("caregiverIds") ?? [];
  const patientIds = filters.getValue("patientIds") ?? [];
  const contractTypeIds = filters.getValue("contractTypeIds") ?? [];
  const officeIds = filters.getValue("officeIds") ?? [];

  const caregiverOptions = (caregiverSearchQuery.data ?? []).map((c) => ({
    label: [c.firstName, c.middleName, c.lastName].filter(Boolean).join(" "),
    value: c.id,
  }));

  const patientOptions = (patientSearchQuery.data ?? []).map((p) => ({
    label: [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" "),
    value: p.id,
  }));

  const templateOptions = (templateQuery.data ?? []).map((t) => ({
    label: t.title,
    value: t.id,
  }));

  const maxEndDate = scheduledFrom
    ? scheduledFrom.plusDays(MAX_DATE_RANGE_DAYS).atStartOfDay()
    : undefined;

  return (
    <Box bg="white" borderRadius="md" mb={4} p={3}>
      <Wrap align="center" spacing={2}>
        <WrapItem>
          <RangeDatePicker
            endDate={scheduledTo}
            inputProps={{ placeholder: "Scheduled date range", size: "sm" }}
            maxDate={maxEndDate}
            startDate={scheduledFrom}
            onChange={handleDateRangeChange}
          />
        </WrapItem>
        <WrapItem>
          <Select
            label="Status"
            multiple={true}
            options={STATUS_OPTIONS}
            size="sm"
            value={statuses.length > 0 ? statuses : null}
            onChange={handleStatusChange}
          />
        </WrapItem>
        <WrapItem>
          <Select
            isLoading={templateQuery.isPending}
            label="Task name"
            multiple={true}
            options={templateOptions}
            size="sm"
            value={taskTemplateIds.length > 0 ? taskTemplateIds : null}
            onChange={handleTaskTemplateChange}
          />
        </WrapItem>
        <WrapItem>
          <Select
            controlledSearchTerm={[caregiverSearchTerm, setCaregiverSearchTerm]}
            isLoading={caregiverSearchQuery.isFetching}
            label="Caregiver"
            multiple={true}
            options={caregiverOptions}
            searchable={true}
            size="sm"
            value={caregiverIds.length > 0 ? caregiverIds : null}
            onChange={handleCaregiverChange}
          />
        </WrapItem>
        <WrapItem>
          <Select
            controlledSearchTerm={[patientSearchTerm, setPatientSearchTerm]}
            isLoading={patientSearchQuery.isFetching}
            label="Patient"
            multiple={true}
            options={patientOptions}
            searchable={true}
            size="sm"
            value={patientIds.length > 0 ? patientIds : null}
            onChange={handlePatientChange}
          />
        </WrapItem>
        <WrapItem>
          <ContractTypeSelect
            buttonProps={{ size: "sm" }}
            multiple={true}
            size="sm"
            value={contractTypeIds.length > 0 ? contractTypeIds : null}
            onChange={handleContractChange}
          />
        </WrapItem>
        <WrapItem>
          <OfficesSelect
            buttonProps={{ size: "sm" }}
            multiple={true}
            size="sm"
            value={officeIds.length > 0 ? officeIds : null}
            onChange={handleOfficeChange}
          />
        </WrapItem>
        <WrapItem>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              filters.resetToDefault();
            }}
          >
            Clear filters
          </Button>
        </WrapItem>
      </Wrap>
    </Box>
  );
}
